import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Provider as JotaiProvider } from "jotai";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { Provider as ReduxProvider } from "react-redux";
import App from "./App";
import "./i18n";

// Import Swiper styles
import "swiper/css";
import "swiper/css/effect-fade";
import "swiper/css/pagination";
import "swiper/css/navigation";
import "./index.css";
import { store } from "./app/store";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// When a lazily-imported chunk fails to load — typically because a new deploy
// replaced the hashed asset that the user's cached index.html referenced — Vite
// fires `vite:preloadError`. Reload once to pull the fresh index.html + chunks.
// Guard with a timestamp so we never loop if the asset is genuinely missing.
window.addEventListener("vite:preloadError", (event) => {
  event.preventDefault();
  const KEY = "vite-preload-reload-ts";
  const last = Number(sessionStorage.getItem(KEY) || 0);
  if (Date.now() - last > 10000) {
    sessionStorage.setItem(KEY, String(Date.now()));
    window.location.reload();
  }
});

const root = document.getElementById("root");

if (!root) {
  throw new Error("Root element not found");
}

createRoot(root).render(
  <QueryClientProvider client={queryClient}>
    <JotaiProvider>
      <ReduxProvider store={store}>
        <GoogleOAuthProvider
          clientId={import.meta.env.VITE_GOOGLE_CLIENT_ID || ""}
        >
          <App />
        </GoogleOAuthProvider>
      </ReduxProvider>
    </JotaiProvider>
  </QueryClientProvider>
);
